﻿import React, { useEffect, useState, useMemo } from "react";
import { Box, IconButton } from "@mui/material";
import {
  ShepDataGridPro,
  ShepCheckbox,
  ShepTooltip,
  ShepGridWarning,
  ShepPagination,
} from "@/components/GenericComponents/ShepComponents";
import WorkflowLookupsToolbar from "./WorkflowLookupsToolbar";
import { useWorkflowLookups } from "./WorkflowLookupsContext";
import {
  WorkflowLookupTypes,
  WorkflowLookupMode,
} from "@/components/Maintenance/MaintenanceTypes";
import EditFillIcon from "@/assets/img/icons/Edit/Fill.svg?react";
import AddThreePLQualifiersPopup from "./AddWorkflowLookup/AddThreePLQualifiersPopup";
import AddDefaultWarehousePopup from "./AddWorkflowLookup/AddDefaultWarehousePopup";
import AddImpliedDCPopup from "./AddWorkflowLookup/AddImpliedDCPopup";
import AddCustomerWarehousePopup from "./AddWorkflowLookup/AddCustomerWarehousePopup";

const WorkflowLookupsTab = (props) => {
  const { selectedTab, rows, fetchData, workflowLookupsLoaded } =
    useWorkflowLookups();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditRow = (row) => {
    setEditRow(row);
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditRow(null);
    setEditDialogOpen(false);
  };

  const allColumnsBase = useMemo(
    () => [
      { field: "id" },
      { field: "WorkflowLookupId", headerName: "WorkflowLookupId", width: 200 },
      {
        field: "WorkflowLookupType",
        headerName: "WorkflowLookupType",
        width: 200,
      },
      {
        field: "CustomerConnectionName",
        headerName: "Customer Connection Name",
        width: 335,
      },
      { field: "Reference", headerName: "ERP Item Code", width: 200 },
      { field: "JSONProperties", headerName: "JSONProperties", width: 200 },
      { field: "CarrierCode", headerName: "3PL", width: 200 },
      { field: "Code1", headerName: "Code 1", width: 150 },
      { field: "Code2", headerName: "Code 2", width: 150 },
      { field: "Code3", headerName: "Code 3", width: 150 },
      { field: "Code4", headerName: "Code 4", width: 150 },
      { field: "Code5", headerName: "Code 5", width: 150 },
      {
        field: "ERPRelatedCustomer",
        headerName: "ERP Related Customer",
        width: 280,
      },
      {
        field: "CustomerShipToCode",
        headerName: "Customer Ship-to Code",
        width: 300,
      },
      { field: "ERPShipToCode", headerName: "ERP Ship-to Code", width: 220 },
      { field: "ERPItemCode", headerName: "ERP Item Code", width: 220 },
      {
        field: "ERPConnectionName",
        headerName: "ERP Connection Name",
        width: 280,
      },
      {
        field: "ERPWarehouseCode",
        headerName: "ERP Warehouse Code",
        width: 280,
      },
      {
        field: "StoreShipToCode",
        headerName: "Store Ship-To Code",
        width: 230,
      },
      {
        field: "DistributionCentreShipToCode",
        headerName: "Distribution Centre Ship-To Code",
        width: 350,
      },

      // Spacer column to push Edit to the far right
      {
        field: "__spacer",
        headerName: "",
        flex: 1,
        minWidth: 0,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
      },

      // Edit action column
      {
        field: "Edit",
        headerName: "",
        width: 60,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
          >
            <ShepTooltip title="Edit">
              <IconButton
                className="workflow-row"
                size="small"
                onClick={() => handleEditRow(params.row)}
                disableRipple
              >
                <EditFillIcon />
              </IconButton>
            </ShepTooltip>
          </Box>
        ),
      },
    ],
    []
  );

  const allColumns = allColumnsBase;

  const tabKeyMap = [
    "threePL",
    "defaultWarehouse",
    "impliedDC",
    "inventoryWarehouse",
  ];

  const visibilityByTab = {
    threePL: {
      CustomerConnectionName: true,
      Code1: true,
      Code2: true,
      Code3: true,
      Code4: true,
      Code5: true,
      // Hide others
      CarrierCode: false,
      Reference: false,
      ERPRelatedCustomer: false,
      ERPShipToCode: false,
      ERPItemCode: false,
      ERPWarehouseCode: false,
      CustomerConnection: false,
      CustomerShipToCode: false,
      StoreShipToCode: false,
      DistributionCentreShipToCode: false,
      id: false,
      WorkflowLookupId: false,
      WorkflowLookupType: false,
      JSONProperties: false,
      ERPConnectionName: false,
    },
    defaultWarehouse: {
      CustomerConnectionName: true,
      ERPRelatedCustomer: true,
      ERPShipToCode: true,
      ERPItemCode: true,
      ERPWarehouseCode: true,
      ERPConnectionName: true,
      // Hide others
      CarrierCode: false,
      Code1: false,
      Code2: false,
      Code3: false,
      Code4: false,
      Code5: false,
      CustomerConnection: false,
      CustomerShipToCode: false,
      StoreShipToCode: false,
      DistributionCentreShipToCode: false,
      id: false,
      WorkflowLookupId: false,
      WorkflowLookupType: false,
      Reference: false,
      JSONProperties: false,
    },
    impliedDC: {
      CustomerConnectionName: true,
      StoreShipToCode: true,
      DistributionCentreShipToCode: true,
      // Hide others
      CustomerShipToCode: false,
      ERPShipToCode: false,
      CarrierCode: false,
      Code1: false,
      Code2: false,
      Code3: false,
      Code4: false,
      Code5: false,
      ERPRelatedCustomer: false,
      ERPItemCode: false,
      ERPWarehouseCode: false,
      id: false,
      WorkflowLookupId: false,
      WorkflowLookupType: false,
      Reference: false,
      JSONProperties: false,
      ERPConnectionName: false,
    },
    inventoryWarehouse: {
      CustomerConnectionName: true,
      ERPWarehouseCode: true,
      ERPConnectionName: true,
      // Hide others
      StoreShipToCode: false,
      DistributionCentreShipToCode: false,
      CustomerShipToCode: false,
      ERPShipToCode: false,
      CarrierCode: false,
      Code1: false,
      Code2: false,
      Code3: false,
      Code4: false,
      Code5: false,
      ERPRelatedCustomer: false,
      ERPItemCode: false,
      id: false,
      WorkflowLookupId: false,
      WorkflowLookupType: false,
      Reference: false,
      JSONProperties: false,
    },
  };

  return (
    <Box sx={{ display: "block", flexDirection: "column", width: "100%" }}>
      <Box
        className="content-box"
        sx={{ display: "flex", flexDirection: "row" }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
            {true ? (
              <ShepDataGridPro
                isDataLoaded={workflowLookupsLoaded}
                rows={rows}
                getRowId={(row) => row.WorkflowLookupId}
                columns={allColumns}
                columnVisibilityModel={
                  visibilityByTab[tabKeyMap[selectedTab]] || {}
                }
                columnHeaderHeight={78}
                rowHeight={100}
                pagination
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 15, page: 1 },
                  },
                  pinnedColumns: {
                    right: ["actions"],
                  },
                  filter: {
                    filterModel: {
                      items: [
                        {
                          field: "WorkflowLookupType",
                          operator: "equals",
                          value:
                            WorkflowLookupTypes.ThreePLQualifiers.value.toString(),
                        },
                      ],
                    },
                  },
                }}
                whiteHeader={true}
                checkboxSelection={true}
                disableMultipleRowSelection={false}
                disableColumnMenu={true}
                disableColumnFilter={true}
                disableColumnReorder={true}
                hideFooterSelectedRowCount={true}
                disableRowSelectionOnClick={!props.canSelect}
                slots={{
                  baseCheckbox: ShepCheckbox,
                  baseTooltip: ShepTooltip,
                  toolbar: WorkflowLookupsToolbar,
                  pagination: ShepPagination,
                }}
                sx={{
                  color: "var(--text-primary)",
                  fontSize: "var(--font-sm)",
                  fontFamily: "var(--font-family)",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                  backgroundColor: "var(--default-light)",
                  border: "none",
                  "& .MuiDataGrid:hover": {
                    backgroundColor: "inherit",
                  },
                  "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                    opacity: 0,
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "var(--tertiary-13)",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "var(--primary-pale)",
                  },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
                    {
                      outline: "none",
                    },
                  "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                    {
                      opacity: 1,
                    },
                  "& .MuiDataGrid-cellCheckbox": {
                    borderLeft: "8px solid transparent",
                  },
                  "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                    opacity: 1,
                    borderLeft: "8px solid var(--primary-main)",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                  },
                  "& .MuiDataGrid-columnHeaderCheckbox": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: "var(--font-md)",
                    fontWeight: "var(--font-bold)",
                    borderLeft: "none",
                  },
                  "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                    {
                      borderBottomLeftRadius: "4px",
                      borderBottomRightRadius: "4px",
                      backgroundColor: "var(--primary-pale)",
                      borderBottom: "none",
                      borderLeft: "none",
                    },
                  "& .MuiDataGrid-columnHeader[data-field='connection']": {
                    marginLeft: props.canSelect ? 0 : "50px",
                  },
                  "& .MuiDataGrid-cell[data-field='connection']": {
                    marginLeft: props.canSelect ? 0 : "50px",
                  },
                }}
              />
            ) : (
              <ShepGridWarning
                title="No workflow lookups to display."
                message="Please add a new workflow lookup or adjust your filters."
                sx={{ marginTop: "25px" }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* reuse existing 'Add' components in 'Edit' mode SHEP-565 */}

      {editDialogOpen &&
        (() => {
          const popupMap = {
            [WorkflowLookupTypes.ThreePLQualifiers.value]:
              AddThreePLQualifiersPopup,
            [WorkflowLookupTypes.DefaultWarehouse.value]:
              AddDefaultWarehousePopup,
            [WorkflowLookupTypes.ImpliedDC.value]: AddImpliedDCPopup,
            [WorkflowLookupTypes.InventoryWarehouse.value]:
              AddCustomerWarehousePopup,
          };

          const PopupComponent = popupMap[selectedTab];
          return PopupComponent ? (
            <PopupComponent
              open={editDialogOpen}
              onClose={() => {
                handleCloseEdit();
                fetchData();
              }}
              workflowLookupType={selectedTab}
              fetchData={fetchData}
              mode={WorkflowLookupMode.Edit}
              existingRow={editRow}
            />
          ) : null;
        })()}
    </Box>
  );
};

export default WorkflowLookupsTab;
